import { useState, useEffect, useMemo } from 'react'
import { connect } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { makeStyles } from '@mui/styles'
import {
  CircularProgress,
  Box,
  Tab,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material'
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarExport,
  GridToolbarQuickFilter,
} from '@mui/x-data-grid'
import Api from '../../api'
import { TabContext, TabList, TabPanel } from '@mui/lab'
import moment from 'moment'
import NoSite from '../../components/NoSite'
import SetupBreadcrumbs from './SetupBreadcrumbs'
import { loadStateFromLocalStorage } from '../../redux/utilsLocalStorage'

const columns = [
  {
    field: 'setup',
    headerName: 'Setups',
    flex: 1.8,
  },
  {
    field: 'setup_capacity',
    headerName: 'Capacity',
    flex: 0.8,
  },
  {
    field: 'd1',
    headerName: 'D-1',
    flex: 0.5,
  },
  {
    field: 'd2',
    headerName: 'D-2',
    flex: 0.5,
  },
  {
    field: 'd3',
    headerName: 'D-3',
    flex: 0.5,
  },
  {
    field: 'd7',
    headerName: 'D-7',
    flex: 0.5,
  },
  {
    field: 'd30',
    headerName: 'D-30',
    flex: 0.5,
  },
  {
    field: 'd180',
    headerName: 'D-180',
    flex: 0.5,
  },
  {
    field: 'd365',
    headerName: 'D-365',
    flex: 0.5,
  },
]

const useStyles = makeStyles(theme => ({
  mainContainer: {
    height: '95%',
    width: '100%',
    padding: '10px 20px',
  },
  tableContainer: {
    height: '80vh',
    width: '100%',
    overflow: 'auto',
  },
  centerItem: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '80vh',
    width: '100%',
  },
}))

const overridesWeirdResizing = {
  '& .MuiDataGrid-main': {
    width: 0,
    minWidth: '100%',
  },
  '& .MuiDataGrid-cell': {
    padding: 0,
    whiteSpace: 'normal',
    wordWrap: 'break-word',
    fontSize: '0.875rem',
    textAlign: 'center',
  },
  '& .MuiDataGrid-columnHeaderTitle': {
    fontWeight: 'bold',
    textAlign: 'center',
  },
  '& .MuiDataGrid-row:hover': {
    cursor: 'pointer',
    color: '#368ED8',
    backgroundColor: '#e3f1fb', // Slightly darker blue on hover
  },
}

export const CustomToolbar = ({
  tableName,
  isAdjustedView,
  handleToggleErrorView,
  isMeteringView,
  handleMeteringToggle,
  activeView,
  handleActiveToggle,
  placeholderName,
  isAdmin = false,
}) => {
  const formattedDate = new Date().toISOString().split('T')[0]
  return (
    <GridToolbarContainer
      style={{ display: 'flex', justifyContent: 'space-between' }}
    >
      {/* <GridToolbarFilterButton /> */}
      <div style={{ display: 'flex' }}>
        <GridToolbarQuickFilter
          debounceMs={300} // Smooth typing experience
          placeholder={`Search ${placeholderName}...`} // Custom placeholder
        />
        <GridToolbarDensitySelector />
        <GridToolbarExport
          csvOptions={{
            delimiter: ';',
            fileName: `${formattedDate}_${tableName}`,
            utf8WithBom: true, // Optional: Add BOM for UTF-8 encoding
          }}
          printOptions={{
            allColumns: true,
            hideFooter: true, // Optionally hide footer during print
            hideToolbar: true, // Optionally hide toolbar during print
            // label: 'PDF',
            // disableToolbarButton: true
          }}
        />
      </div>

      <ToggleButtonGroup
        size="small"
        color="primary"
        value={activeView}
        exclusive
        onChange={(_, newValue) => {
          if (newValue !== null) {
            handleActiveToggle(newValue)
          }
        }}
        aria-label="status-toggle"
      >
        <ToggleButton value="all">All</ToggleButton>
        <ToggleButton value="active">Active</ToggleButton>
        {placeholderName === 'Plant' && isAdmin && (
          <ToggleButton value="aboveThreshold">Above Threshold</ToggleButton>
        )}
      </ToggleButtonGroup>

      <ToggleButtonGroup
        size="small"
        color="primary"
        value={isMeteringView ? 'metering' : 'online'}
        exclusive
        onChange={(event, newValue) => {
          if (newValue !== null) {
            handleMeteringToggle(newValue === 'metering')
          }
        }}
        aria-label="error-type-toggle"
      >
        <ToggleButton value="online">Online</ToggleButton>
        <ToggleButton value="metering">Metering</ToggleButton>
      </ToggleButtonGroup>

      <ToggleButtonGroup
        size="small"
        color="primary"
        value={isAdjustedView ? 'percentageError' : 'capacityAdjustedError'}
        exclusive
        onChange={(event, newValue) => {
          if (newValue !== null) {
            handleToggleErrorView(newValue === 'percentageError')
          }
        }}
        aria-label="error-view-toggle"
      >
        <ToggleButton value="percentageError">Percentage Error</ToggleButton>
        <ToggleButton value="capacityAdjustedError">
          Capacity Adjusted Error
        </ToggleButton>
      </ToggleButtonGroup>
    </GridToolbarContainer>
  )
}

export const dataKeys = ['d1', 'd2', 'd3', 'd7', 'd30', 'd180', 'd365']
export const findGlobalMinMaxFromKeys = (data, keys) => {
  let min = Infinity
  let max = -Infinity

  for (let i = 0; i < data.length; i++) {
    const obj = data[i]

    for (let j = 0; j < keys.length; j++) {
      const key = keys[j]
      const val = obj[key]

      if (val != null) {
        if (val < min) min = val
        if (val > max) max = val
      }
    }
  }

  return { min, max }
}

export const getColorGreenWhiteRed = (value, min, max) => {
  if (typeof value !== 'number' || !isFinite(value)) return 'transparent'

  if (max === min) return '#FFFFFF' // Neutral if values are all the same

  const mid = (min + max) / 2

  let r, g, b

  if (value <= mid) {
    const t = (value - min) / (mid - min)
    r = Math.round(255 * t)
    g = 255
    b = Math.round(255 * t)
  } else {
    const t = (value - mid) / (max - mid)
    r = 255
    g = Math.round(255 * (1 - t))
    b = Math.round(255 * (1 - t))
  }

  return rgbToHex(r, g, b)
}

const rgbToHex = (r, g, b) => {
  return (
    '#' +
    [r, g, b]
      .map(x => {
        const hex = x.toString(16)
        return hex.length === 1 ? '0' + hex : hex
      })
      .join('')
  )
}

export const mapData = (data, isSetup) => {
  return Object.entries(data).map(([setup, values]) => ({
    [isSetup ? 'setup' : 'plant_name']: setup,
    ...Object.fromEntries(
      Object.entries(values).map(([key, value]) => [
        key,
        typeof value === 'number' ? Number(value.toFixed(2)) : value,
      ]),
    ),
  }))
}

export const getNormalizedUnit = unit => {
  const unitMapping = {
    kW: 'kW',
    kWh: 'kW',
    MW: 'MW',
    MWh: 'MW',
  }
  return unitMapping[unit] || 'kW'
}

export const adjustDataWithCapacity = (data, selectedUnit, input_capacity) => {
  return data.map(item => {
    const adjustedItem = { ...item }
    const rawCapacity = item[input_capacity]
    const capacity = selectedUnit === 'MW' ? rawCapacity / 1000 : rawCapacity

    dataKeys.forEach(key => {
      const value = item[key]
      if (value != null && typeof value === 'number') {
        adjustedItem[key] = +((value * capacity) / 100).toFixed(2)
      }
    })

    return adjustedItem
  })
}

const QualityMonitoringSetups = props => {
  const classes = useStyles(props)
  const location = useLocation()
  const {
    history,
    mainTabValue,
    setMainTabValue,
    tabsMap,
    activeView,
    handleActiveToggle,
    user,
  } = props
  const [qualityMonitoringDataCache, setQualityMonitoringDataCache] = useState(
    {},
  )
  const [qualityMonitoringData, setQualityMonitoringData] = useState([])
  const [loading, setLoading] = useState(false)
  const [nodataContent, setNodataContent] = useState({})

  const backupStoredUnit = loadStateFromLocalStorage('unit') || 'kW'
  const selectedUnit = getNormalizedUnit(user.unit || backupStoredUnit)

  const [isAdjustedView, setIsAdjustedView] = useState(true)
  const [
    qualityMonitoringAdjustedDataCache,
    setQualityMonitoringAdjustedDataCache,
  ] = useState({})

  const [isMeteringView, setIsMeteringView] = useState(false) // false means online by default

  const [paginationByTab, setPaginationByTab] = useState({
    1: { page: 0, pageSize: 50 },
    2: { page: 0, pageSize: 50 },
    3: { page: 0, pageSize: 50 },
  })

  const filteredData = useMemo(() => {
    if (activeView === 'all') return qualityMonitoringData

    if (activeView === 'active') {
      return qualityMonitoringData.filter(item => item.setup_capacity > 0)
    }

    return qualityMonitoringData // fallback, just in case
  }, [qualityMonitoringData, activeView])

  const { min, max } = findGlobalMinMaxFromKeys(filteredData, dataKeys)

  const colorizeData = useMemo(() => {
    return columns.map(item => {
      return {
        ...item,
        headerName:
          item.field === 'setup_capacity'
            ? selectedUnit === 'MW'
              ? 'Capacity (MW)'
              : 'Capacity (kW)'
            : item.headerName,
        headerAlign: 'center',
        renderCell: params => {
          const rowCapacity = params.row?.setup_capacity
          const cellValue = params.value

          // If entire row grey (setup_capacity === 0)
          if (rowCapacity === 0) {
            return (
              <Box
                sx={{
                  backgroundColor: '#D0D0D0', // grey background for whole row
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#888888', // optional grey text
                }}
              >
                {/* Show empty if null, else show value */}
                {cellValue === null ? '' : cellValue}
              </Box>
            )
          }

          // For setup_capacity cell when rowCapacity !== 0
          if (item.field === 'setup_capacity') {
            if (cellValue == null) return null

            const displayValue =
              selectedUnit === 'MW' ? (cellValue / 1000).toFixed(2) : cellValue

            return (
              <Box
                sx={{
                  backgroundColor: 'transparent',
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {displayValue}
              </Box>
            )
          }

          // For other cells when rowCapacity !== 0
          // Do NOT grey null cells, just render empty normally
          if (cellValue === null) {
            return (
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              />
            )
          }

          // Normal colorize cells with value
          const backgroundColor = getColorGreenWhiteRed(cellValue, min, max)

          return (
            <Box
              sx={{
                backgroundColor,
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {cellValue}
            </Box>
          )
        },
      }
    })
  }, [columns, selectedUnit, min, max])

  useEffect(() => {
    const fetchData = async () => {
      const tabKey = mainTabValue.name.toLowerCase()
      const cacheKey = `${tabKey}_${isMeteringView ? 'metering' : 'online'}`

      // Check if we have cached data for this specific combination
      if (qualityMonitoringDataCache[cacheKey]) {
        setQualityMonitoringData(qualityMonitoringDataCache[cacheKey])
        return
      }

      setLoading(true)
      const today = new Date()
      const qualityClientObj = {
        client: loadStateFromLocalStorage('username'),
        period: tabKey,
        date: moment(today).format('YYYY-MM-DD'),
        type: isMeteringView ? 'metering' : 'online',
      }

      try {
        const response =
          await Api.QualityMonitoring.getQualityMonitoringSetups(
            qualityClientObj,
          )

        if (response.status === 200) {
          const formattedData = mapData(response.data, true)
          setQualityMonitoringData(formattedData)

          // Cache it
          setQualityMonitoringDataCache(prev => ({
            ...prev,
            [cacheKey]: formattedData,
          }))
        }

        setLoading(false)
      } catch (error) {
        console.error(`Error while fetching ${mainTabValue.name}:`, error)
        setNodataContent({
          heading: `No data found for period : ${mainTabValue.name}`,
          body: '',
        })
        setLoading(false)
      }
    }

    fetchData()
  }, [mainTabValue.name, isMeteringView, selectedUnit])

  const handleSelectedSetup = params => {
    if (params.row.setup_capacity === 0) return
    history.push(`${location.pathname}/${params.id}`)
  }

  const handleMainTabChange = (_, newValue) => {
    const selectedTab = tabsMap.find(tab => tab.id === newValue)
    setMainTabValue(selectedTab)
  }

  const handleToggleErrorView = nextIsAdjustedView => {
    const tabKey = mainTabValue.name.toLowerCase()
    const cacheKey = `${tabKey}_${isMeteringView ? 'metering' : 'online'}`

    // No change (clicked the already selected button)
    if (nextIsAdjustedView === isAdjustedView) return

    if (nextIsAdjustedView) {
      // Switching TO Percentage Error / Initial
      if (qualityMonitoringDataCache[cacheKey]) {
        setQualityMonitoringData(qualityMonitoringDataCache[cacheKey])
      }
    } else {
      // Switching TO Capacity Adjusted Error
      if (qualityMonitoringAdjustedDataCache[cacheKey]) {
        setQualityMonitoringData(qualityMonitoringAdjustedDataCache[cacheKey])
      } else {
        // IMPORTANT: adjust from the base/original dataset
        const base =
          qualityMonitoringDataCache[cacheKey] ?? qualityMonitoringData

        const adjusted = adjustDataWithCapacity(
          base,
          selectedUnit,
          'setup_capacity',
        )

        setQualityMonitoringData(adjusted)
        setQualityMonitoringAdjustedDataCache(prev => ({
          ...prev,
          [cacheKey]: adjusted,
        }))
      }
    }

    setIsAdjustedView(nextIsAdjustedView)
  }

  const handlePaginationChange = model => {
    setPaginationByTab(prev => ({
      ...prev,
      [mainTabValue.id]: model,
    }))
  }

  const handleMeteringToggle = isMetering => {
    setIsMeteringView(isMetering)
    // Clear the cache when switching types to force a new API call
    // setQualityMonitoringDataCache({})
    // setQualityMonitoringAdjustedDataCache({})
  }

  const renderDataGrid = () => {
    const currentPagination = paginationByTab[mainTabValue.id] || {
      page: 0,
      pageSize: 50,
    }

    return (
      <div className={classes.tableContainer}>
        <DataGrid
          rows={filteredData}
          rowHeight={30}
          columns={colorizeData}
          getRowId={row => row.setup}
          disableColumnSelector
          disableColumnMenu
          filterMode="client"
          paginationModel={currentPagination}
          onPaginationModelChange={handlePaginationChange}
          pageSizeOptions={[50]}
          autoPageSize={false}
          slots={{
            toolbar: props => (
              <CustomToolbar
                {...props}
                tableName={mainTabValue.name}
                isAdjustedView={isAdjustedView}
                handleToggleErrorView={handleToggleErrorView}
                isMeteringView={isMeteringView}
                handleMeteringToggle={handleMeteringToggle}
                activeView={activeView}
                handleActiveToggle={handleActiveToggle}
                placeholderName={'Setup'}
              />
            ),
          }}
          slotProps={{
            toolbar: {
              showQuickFilter: true, // Enables search bar
              quickFilterProps: { debounceMs: 300 },
            },
          }}
          sx={overridesWeirdResizing}
          onRowClick={handleSelectedSetup}
          localeText={{
            toolbarExportPrint: 'Print as PDF',
            toolbarExport: 'Export',
          }}
        />
      </div>
    )
  }

  return (
    <div className={classes.mainContainer}>
      <SetupBreadcrumbs history={history} activeView={activeView} />
      <TabContext value={mainTabValue.id}>
        <Box sx={{ borderBottom: 0 }}>
          <TabList
            onChange={handleMainTabChange}
            aria-label="Quality monitoring"
            sx={{
              // indicator + selected color
              '& .MuiTabs-indicator': { backgroundColor: '#368ED8' },
              '& .MuiTab-root.Mui-selected': { color: '#368ED8' },
              '& .MuiTab-root': {
                minWidth: '160px',
              },
            }}
          >
            <Tab label="ID" value="1" />
            <Tab label="DAH" value="2" />
            {/* <Tab
              
              label="Composite"
              value="3"
            /> */}
          </TabList>
        </Box>

        {/* Loading Spinner */}
        {loading ? (
          <div className={classes.centerItem}>
            <CircularProgress />
          </div>
        ) : qualityMonitoringData.length !== 0 ? (
          <>
            <TabPanel value="1" sx={{ p: '30px 0' }}>
              <div>{renderDataGrid()}</div>
            </TabPanel>

            <TabPanel value="2" sx={{ p: '30px 0' }}>
              <div>{renderDataGrid()}</div>
            </TabPanel>

            <TabPanel value="3" sx={{ p: '30px 0' }}>
              <div>{renderDataGrid()}</div>
            </TabPanel>
          </>
        ) : (
          <div className={classes.centerItem}>
            <div className={classes.alignNoData}>
              <NoSite
                heading={nodataContent.heading}
                body={nodataContent.body}
              />
            </div>
          </div>
        )}
      </TabContext>
    </div>
  )
}

const mapStateToProps = state => ({
  user: state.user,
})

export default connect(mapStateToProps)(QualityMonitoringSetups)
